import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setSessionToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  isFirebaseConfigured, signInWithGoogle, signInWithApple, signOutFirebase,
  ensureFirebaseReady,
} from "../lib/firebase";

/**
 * FirebaseAuthButtons — shows Google + Apple sign-in via Firebase when the
 * app has REACT_APP_FIREBASE_* env vars configured. On successful sign-in
 * it exchanges the ID token with our backend at POST /api/auth/firebase/session
 * which mints a normal session cookie — the rest of the app is unchanged.
 *
 * Hidden entirely if Firebase config is missing (dual-run migration UX).
 */
export default function FirebaseAuthButtons({ compact = false, redirectTo = "/app", googleEnabled = true, appleEnabled = true }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(null);   // "google" | "apple" | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // We only need the *client-side* config (Firebase Web SDK's apiKey +
      // projectId + appId). If those load — either from build-time env vars
      // or the public-config endpoint — the buttons are safe to render.
      // The backend Admin-SDK enabled probe (`/auth/firebase/status`)
      // used to gate this too, but on Capacitor iOS the axios call was
      // flaky (CORS + credentials), causing the buttons to hide even when
      // everything actually worked. Any real Admin-SDK failure surfaces
      // clearly on the /auth/firebase/session exchange after sign-in.
      const cfgOk = await ensureFirebaseReady();
      if (!cancelled) setReady(!!cfgOk);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!ready) return null;

  const doLogin = async (kind) => {
    setBusy(kind);
    try {
      const idToken = kind === "google" ? await signInWithGoogle() : await signInWithApple();
      const { data } = await api.post("/auth/firebase/session", {
        id_token: idToken,
        referral_code: localStorage.getItem("ref_code") || undefined,
      });
      if (data?.session_token) setSessionToken(data.session_token);
      if (data?.user) localStorage.removeItem("ref_code");
      // Hydrate AuthContext synchronously so ProtectedRoute on the redirect
      // target sees the user in its very first render pass.
      if (data?.user) setUser(data.user);
      toast.success(`Signed in as ${data?.user?.name || data?.user?.email}`);
      // On native, sidestep the React-router / setState batching race that
      // caused "welcome toast but no navigation" — see EmailPasswordAuthNative.
      if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
        window.location.assign(redirectTo);
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      // v88.8 — Surface the error IMMEDIATELY. Previously we did the local
      // signOut cleanup FIRST, but signOutFirebase's Firebase JS SDK call
      // (`fbSignOut`) can hang inside Capacitor WKWebView the same way
      // signInWithCredential did — blocking the toast.error below forever
      // and giving the user a "silent stuck" experience. Now: log +
      // toast + reset UI first, cleanup last (fire-and-forget on native).
      // `detail` from FastAPI can be a string, array of {msg}, or an object.
      // Always coerce to a string before handing to the toast library, else
      // React throws "The object is not a valid React child".
      const raw = err?.response?.data?.detail ?? err?.message ?? "Sign-in failed";
      let msg;
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((e) => e?.msg || "").filter(Boolean).join(" ") || "Sign-in failed";
      else if (raw && typeof raw.msg === "string") msg = raw.msg;
      else msg = "Sign-in failed";
      // Xcode / adb logcat visibility — critical for RCA on device where
      // devtools access is limited.
      console.error("[FirebaseAuthButtons] sign-in failed:", {
        message: msg,
        status: err?.response?.status,
        detail: err?.response?.data,
        rawError: err?.message,
      });
      if (/popup-closed-by-user|cancelled-popup/i.test(msg)) {
        // User dismissed the popup — silent, no toast.
      } else {
        toast.error(msg);
      }
      // Best-effort local sign-out so the next click starts from a clean state.
      // Fire-and-forget on native so a hang can't block the finally block or
      // the toast above. Also swallow any throw — this is cleanup, not the
      // primary code path.
      signOutFirebase().catch(() => { /* non-fatal cleanup */ });
    } finally {
      setBusy(null);
    }
  };

  // Per product requirement: Apple Sign In is required on iOS only.
  // On web and Android we don't show the button at all.
  // v146.13 — Super Admin can additionally hide Google / Apple entirely
  // via feature flags; when both are hidden we render nothing so the
  // wrapping "OR CONTINUE WITH" card also collapses.
  const showAppleBtn = appleEnabled
    && typeof window !== "undefined"
    && window.Capacitor?.isNativePlatform?.()
    && window.Capacitor?.getPlatform?.() === "ios";
  const showGoogleBtn = googleEnabled;
  if (!showGoogleBtn && !showAppleBtn) return null;

  return (
    <div className={compact ? "flex gap-2" : "flex flex-col gap-2 w-full"}
         data-testid="firebase-auth-buttons">
      {showGoogleBtn && (
        <button
          onClick={() => doLogin("google")}
          disabled={!!busy}
          data-testid="firebase-signin-google"
          className="border-2 border-ink bg-white text-ink px-4 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
        >
          {busy === "google" ? "Signing in…" : (
            <>
              <GoogleGlyph /> Continue with Google
            </>
          )}
        </button>
      )}
      {showAppleBtn && (
        <button
          onClick={() => doLogin("apple")}
          disabled={!!busy}
          data-testid="firebase-signin-apple"
          className="border-2 border-ink bg-ink text-white px-4 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
        >
          {busy === "apple" ? "Signing in…" : (
            <>
              <AppleGlyph /> Continue with Apple
            </>
          )}
        </button>
      )}
    </div>
  );
}

/* Inline vector glyphs — avoids adding another icon dependency. */
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.09-1.93 3.27-4.77 3.27-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.93-6.13-4.52H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.87 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.69-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.69 2.84C6.73 7.31 9.15 5.38 12 5.38z"/>
    </svg>
  );
}
function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M16.365 1.43c0 1.14-.42 2.22-1.24 3.06-.83.83-1.9 1.45-3.02 1.36-.11-1.12.42-2.24 1.19-3.03.85-.86 2.02-1.5 3.07-1.55v.16zm4.09 16.32c-.32.73-.51 1.05-.93 1.7-.58.91-1.4 2.04-2.42 2.05-.9.01-1.14-.6-2.36-.59-1.22.01-1.48.6-2.38.59-1.02-.01-1.79-1.03-2.38-1.94-1.6-2.5-1.77-5.44-.78-7.01.7-1.11 1.81-1.77 2.85-1.77 1.06 0 1.73.58 2.61.58.85 0 1.37-.58 2.6-.58.92 0 1.9.5 2.6 1.36-2.29 1.26-1.92 4.54.6 5.61z"/>
    </svg>
  );
}
